import { createHeadersFormOptions } from "../dataProvider/fetch";
import { checkAppVersion } from "../dataProvider/utils";
import { handleAuthStatus } from "./authSession";
import { buildApiUrl, extractErrorMessage } from "../dataProvider/requestJson";

/**
 * Admin tools (Tools page). System administrators only.
 *
 * Failures carry `error.status`: 409 means "a run is already active", 404 an unknown meet or
 * event, 400 a refusal whose message comes from the backend (e.g. a championship event).
 */
const STATUS_MESSAGES = {
  401: "Your session has expired. Log in again.",
  403: "Only system administrators can use these tools.",
};

const enc = encodeURIComponent;

export const TOOL_PATHS = {
  aauRuns: ({ page = 0, size = 10 } = {}) =>
    `admin/aau-reverification-runs?page=${page}&size=${size}`,
  aauRun: (id) => `admin/aau-reverification-runs/${enc(id)}`,
  startAauRun: (dryRun) =>
    `admin/aau-reverification-runs?dryRun=${dryRun ? "true" : "false"}`,
  eventOrderCollisions: (includePast) =>
    `admin/meets/event-order/collisions?includePast=${
      includePast ? "true" : "false"
    }`,
  eventOrderPreview: (meetId) => `admin/meets/${enc(meetId)}/event-order`,
  eventOrderRepair: (meetId) => `admin/meets/${enc(meetId)}/event-order/repair`,
  unfinalize: (eventId) => `admin/events/${enc(eventId)}/unfinalize`,
};

const request = async (path, { method = "GET" } = {}) => {
  const options = { method };
  if (method !== "GET") options.body = "{}";
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers: createHeadersFormOptions(options),
  });
  const text = await response.text();
  checkAppVersion(response.headers.get("x-version"));
  handleAuthStatus(response.status);
  if (!response.ok) {
    const error = new Error(
      extractErrorMessage(text, response.status, STATUS_MESSAGES),
    );
    error.status = response.status;
    throw error;
  }
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (_) {
    return text;
  }
};

const post = (path) => request(path, { method: "POST" });

/** Run states after which nothing changes any more. */
export const FINISHED_RUN_STATUSES = [
  "COMPLETED",
  "ABORTED",
  "SKIPPED",
  "FAILED",
];

export const isRunActive = (run) =>
  Boolean(run) && !FINISHED_RUN_STATUSES.includes(run.status);

export const adminToolsApi = {
  listAauRuns: (params) => request(TOOL_PATHS.aauRuns(params)),
  getAauRun: (id) => request(TOOL_PATHS.aauRun(id)),
  startAauRun: (dryRun) => post(TOOL_PATHS.startAauRun(dryRun)),

  eventOrderCollisions: (includePast) =>
    request(TOOL_PATHS.eventOrderCollisions(includePast)),
  previewEventOrder: (meetId) => request(TOOL_PATHS.eventOrderPreview(meetId)),
  repairEventOrder: (meetId) => post(TOOL_PATHS.eventOrderRepair(meetId)),

  previewUnfinalize: (eventId) => request(TOOL_PATHS.unfinalize(eventId)),
  unfinalize: (eventId) => post(TOOL_PATHS.unfinalize(eventId)),
};

/** A positive whole number typed into an id box, or null. */
export const parseId = (value) => {
  const trimmed = String(value ?? "").trim();
  return /^[1-9]\d*$/.test(trimmed) ? Number(trimmed) : null;
};
